import * as React from "react";
import { PopoverContent, PopoverAnchor } from "./ui/popover";
import { ChatInputArea } from "./ChatInputArea";
import { ActionButton } from "./ActionButton";
import RequestCommandMenu from "./RequestCommandMenu";
import type { Request } from "@/types/research.types";
import { useRequestGallery } from "./request-gallery/RequestGalleryContext";
import ExecuteAutomationDialog from "./request-gallery/ExecuteAutomationDialog";

type MenuType = "slash" | "hash" | null;
type ChainProgressState = { current: number; total: number } | null;

export const renderPopoverContent = (
  activeMenu: MenuType,
  currentRequests: Request[],
  selectedIndex: number,
  handleCommandSelect: (c: string, request?: Request) => void,
  handleInteractOutside: (e: Event) => void,
  textareaRef: React.RefObject<HTMLTextAreaElement | null>
) => (
  <PopoverContent
    className="w-[calc(100%-80px)] min-w-[350px] max-w-[500px] p-0 mb-2"
    onOpenAutoFocus={(e) => e.preventDefault()}
    onCloseAutoFocus={(e) => {
      e.preventDefault();
      textareaRef.current?.focus();
    }}
    onInteractOutside={handleInteractOutside}
    align="start"
    side="top"
  >
    <RequestCommandMenu
      heading={activeMenu === "slash" ? "Simple Requests" : "Chained Requests"}
      requests={currentRequests}
      onSelect={handleCommandSelect}
      selectedId={currentRequests[selectedIndex]?.id ?? ""}
    />
  </PopoverContent>
);

export const renderActionButtons = (
  isRecording: boolean,
  isMicroLoading: boolean,
  isConverting: boolean,
  isSendingMessage: boolean,
  chatMessage: string,
  startDictation: () => void,
  handleSendMessage: () => void
) => (
  <div className="flex gap-2 items-center">
    <ActionButton
      isRecording={isRecording}
      isMicroLoading={isMicroLoading}
      isConverting={isConverting}
      isSendingMessage={isSendingMessage}
      chatMessage={chatMessage}
      onMicClick={startDictation}
      onSend={handleSendMessage}
    />
  </div>
);

export const renderChatInputArea = (props: {
  galleryId: string;
  requestType: "discuss" | "research";
  openGallery: ReturnType<typeof useRequestGallery>["openGallery"];
  handleRequestSelect: (content: string, request?: Request) => Promise<void>;
  isRecording: boolean;
  isMicroLoading: boolean;
  isSpeaking: boolean;
  textareaRef: React.RefObject<HTMLTextAreaElement | null>;
  chatMessage: string;
  isSendingMessage: boolean;
  handleTextareaChange: (event: React.ChangeEvent<HTMLTextAreaElement>) => void;
  handleKeyPress: (e: React.KeyboardEvent) => void;
  showCompassButton: boolean;
  handleCompassClick?: () => void;
  compassAssetSrc?: string;
  chainProgress: ChainProgressState;
}) => (
  <PopoverAnchor asChild>
    <ChatInputArea
      galleryId={props.galleryId}
      requestType={props.requestType}
      handleOpenRequestGallery={() =>
        props.openGallery(props.galleryId, {
          type: props.requestType,
          onSelect: props.handleRequestSelect,
        })
      }
      isRecording={props.isRecording}
      isMicroLoading={props.isMicroLoading}
      isSpeaking={props.isSpeaking}
      textareaRef={props.textareaRef}
      chatMessage={props.chatMessage}
      isSendingMessage={props.isSendingMessage}
      handleInternalTextareaChange={props.handleTextareaChange}
      handleInternalKeyPress={props.handleKeyPress}
      showCompassButton={props.showCompassButton}
      handleCompassClick={props.handleCompassClick}
      compassAssetSrc={props.compassAssetSrc}
      chainProgress={props.chainProgress}
    />
  </PopoverAnchor>
);

export const ChatInputBody: React.FC<{
  state: {
    galleryId: string;
    textareaRef: React.RefObject<HTMLTextAreaElement | null>;
    chatMessage: string;
    activeMenu: "slash" | "hash" | null;
    selectedIndex: number;
    openGallery: ReturnType<typeof useRequestGallery>["openGallery"];
    chainProgress: ChainProgressState;
  };
  requestType: "discuss" | "research";
  isSendingMessage: boolean;
  showCompassButton: boolean;
  handleCompassClick?: () => void;
  compassAssetSrc?: string;
  isRecording: boolean;
  isMicroLoading: boolean;
  isSpeaking: boolean;
  isConverting: boolean;
  startDictation: () => void;
  handleRequestSelect: (content: string, request?: Request) => Promise<void>;
  handleTextareaChange: (event: React.ChangeEvent<HTMLTextAreaElement>) => void;
  handleKeyPress: (e: React.KeyboardEvent) => void;
  handleCommandSelect: (c: string, request?: Request) => void;
  handleSendMessage: () => void;
  handleInteractOutside: (e: Event) => void;
  currentRequests: Request[];
}> = (props) => (
  <>
    {renderChatInputArea({
      galleryId: props.state.galleryId,
      requestType: props.requestType,
      openGallery: props.state.openGallery,
      handleRequestSelect: props.handleRequestSelect,
      isRecording: props.isRecording,
      isMicroLoading: props.isMicroLoading,
      isSpeaking: props.isSpeaking,
      textareaRef: props.state.textareaRef,
      chatMessage: props.state.chatMessage,
      isSendingMessage: props.isSendingMessage,
      handleTextareaChange: props.handleTextareaChange,
      handleKeyPress: props.handleKeyPress,
      showCompassButton: props.showCompassButton,
      handleCompassClick: props.handleCompassClick,
      compassAssetSrc: props.compassAssetSrc,
      chainProgress: props.state.chainProgress,
    })}
    {renderPopoverContent(
      props.state.activeMenu,
      props.currentRequests,
      props.state.selectedIndex,
      props.handleCommandSelect,
      props.handleInteractOutside,
      props.state.textareaRef
    )}
    {renderActionButtons(
      props.isRecording,
      props.isMicroLoading,
      props.isConverting,
      props.isSendingMessage,
      props.state.chatMessage,
      props.startDictation,
      props.handleSendMessage
    )}
  </>
);

export const ChainConfirmDialog: React.FC<{
  pendingChainRequest: Request | null;
  onClose: () => void;
  handleRequestSelect: (content: string, request?: Request) => Promise<void>;
}> = ({ pendingChainRequest, onClose, handleRequestSelect }) => (
  <ExecuteAutomationDialog
    isOpen={pendingChainRequest != null}
    onClose={onClose}
    onConfirm={() => {
      if (pendingChainRequest == null) return;
      const request = pendingChainRequest;
      onClose();
      void handleRequestSelect(request.content, request);
    }}
    request={pendingChainRequest}
  />
);
